import { useEffect, useState } from "react";
import { coverStyle } from "@/lib/cover";
import { useParams } from "react-router-dom";
import axios from "axios";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart, Calendar, MapPin, Check, X, Clock, Plus, Trash2 } from "lucide-react";
import { to12h } from "@/lib/time";
import { RsvpAuthGate } from "@/components/RsvpAuthGate";
import { RsvpVerifyNotice } from "@/components/RsvpVerifyNotice";

const DEFAULT_MEALS = ["Chicken", "Beef", "Fish", "Vegetarian", "Vegan"];

export default function PublicRSVP() {
  const { token } = useParams();
  const { user, login, register, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [guestInfo, setGuestInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/rsvp/${token}`)
      .then(({ data }) => {
        setData(data);
        const init = {};
        (data.party || []).forEach((g) => {
          init[g.id] = {
            guest_id: g.id,
            status: g.rsvp_status && g.rsvp_status !== "pending" ? g.rsvp_status : "",
            meal_choice: g.meal_choice || "", dietary: g.dietary || "",
            song_request: g.song_request || "", accessibility: g.accessibility || "",
            plus_one_attending: !!g.plus_one_attending, plus_ones: toPlusList(g), message: g.message || "",
          };
        });
        setAnswers(init);
      })
      .catch(() => setNotFound(true));
  }, [token]);

  if (notFound) return <Centered><p className="font-serif text-3xl text-stone-500">Invitation not found</p></Centered>;
  if (!data) return <Centered><p className="font-serif text-3xl text-stone-300">Loading…</p></Centered>;

  const { wedding, party, rsvp_config: cfg, invitation } = data;
  const title = (invitation?.custom_title || "").trim() || wedding.title || (wedding.partner_two ? `${wedding.partner_one} & ${wedding.partner_two}` : wedding.partner_one);
  const setAns = (gid, patch) => setAnswers((a) => ({ ...a, [gid]: { ...a[gid], ...patch } }));

  const submit = async () => {
    const list = Object.values(answers);
    if (list.some((a) => !a.status)) { toast.error("Please choose Attending or Declined for each guest."); return; }
    setSaving(true);
    try {
      if (guestInfo) {
        await axios.post(`${API}/public/rsvp/${token}/guest-submit`, { ...guestInfo, answers: list });
      } else {
        await api.post(`/rsvp/${token}/submit`, { answers: list });
      }
      setSubmitted(true);
      toast.success("Your RSVP has been recorded and saved to your Evermore memories.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FDFBF7" }}>
      <div className="relative h-72 sm:h-96 overflow-hidden">
        <img src={wedding.cover_image_url} alt="venue" className="absolute inset-0 w-full h-full object-cover" style={coverStyle(wedding)} />
        <div className="absolute inset-0 bg-black/35" />
        <div className="relative h-full flex flex-col items-center justify-center text-center text-white px-6">
          <Heart size={22} fill="white" className="mb-3" />
          <p className="overline text-white/70">You're invited to</p>
          <h1 className="font-serif text-5xl sm:text-6xl tracking-tight mt-2" data-testid="rsvp-title">{title}</h1>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 mt-5 text-sm text-white/85">
            {wedding.date && <span className="flex items-center gap-1.5"><Calendar size={14} /> {formatDate(wedding.date)}</span>}
            {wedding.venue_name && <span className="flex items-center gap-1.5"><MapPin size={14} /> {wedding.venue_name}</span>}
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-5 py-12">
        {invitation?.message && <p className="text-center text-stone-500 mb-8 fade-up">{invitation.message}</p>}

        {submitted ? (
          <div className="bg-white border border-[#E8E6E1] rounded-xl p-10 text-center fade-up" data-testid="rsvp-success">
            <div className="w-14 h-14 rounded-full bg-[#8C9A86]/15 grid place-items-center mx-auto mb-4"><Check size={26} className="text-[#8C9A86]" /></div>
            <h2 className="font-serif text-3xl">Thank you!</h2>
            <p className="text-stone-500 mt-2 text-sm">Your response has been recorded and this event is now saved to your Evermore memories.</p>
            <div className="mt-6 flex flex-col gap-2">
              <button onClick={() => setSubmitted(false)} className="text-sm text-[#8C9A86] hover:underline">Change my response</button>
              <a href="/app" className="text-sm text-[#8C9A86] hover:underline" data-testid="go-memories-link">Go to my Evermore memories →</a>
            </div>
          </div>
        ) : (!user && !guestInfo) ? (
          <RsvpAuthGate onLogin={login} onRegister={register} onNoAccount={(info) => setGuestInfo(info)} defaultEmail={party?.[0]?.email || ""} redirectTo={window.location.pathname} />
        ) : (user && !user.email_verified) ? (
          <div className="max-w-md mx-auto"><RsvpVerifyNotice email={user.email} redirect={window.location.pathname} onRefresh={refreshUser} /></div>
        ) : (
          <div className="space-y-6 fade-up">
            <p className="text-center text-sm text-stone-500">
              {party.length > 1
                ? `You're responding for ${party.length} guests. Please complete each one below.`
                : "Please complete your response below."}
            </p>
            {party.map((g) => (
              <GuestRsvpCard key={g.id} guest={g} cfg={cfg} ans={answers[g.id]} setAns={(patch) => setAns(g.id, patch)} />
            ))}
            <button onClick={submit} disabled={saving} data-testid="rsvp-submit"
              className="w-full rounded-full bg-[#2C2A28] text-white py-3 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-40">
              {saving ? "Sending…" : "Send response"}
            </button>
          </div>
        )}

        {wedding.sub_events?.length > 0 && (
          <div className="mt-10">
            <p className="overline text-center mb-5">Schedule</p>
            <div className="space-y-3">
              {wedding.sub_events.map((s) => (
                <div key={s.id} className="bg-white border border-[#E8E6E1] rounded-lg px-5 py-4 flex items-center gap-4">
                  <Clock size={16} className="text-[#8C9A86]" />
                  <div>
                    <p className="text-sm font-medium text-stone-800">{s.name}</p>
                    <p className="text-xs text-stone-400">{[to12h(s.time), s.venue_name || s.location, s.address].filter(Boolean).join(" · ")}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center mt-12">
          <a href={`/w/${wedding.public_slug}`} className="text-sm text-[#8C9A86] hover:underline" data-testid="view-wedding-link">Visit the event page →</a>
        </div>
      </div>
    </div>
  );
}

function GuestRsvpCard({ guest, cfg, ans, setAns }) {
  if (!ans) return null;
  const meals = (cfg?.meal_options?.length ? cfg.meal_options : DEFAULT_MEALS);
  const allowance = guest.allowed_plus_ones || 0;
  const plus = ans.plus_ones || [];
  const setPlus = (i, patch) => setAns({ plus_ones: plus.map((x, idx) => (idx === i ? { ...x, ...patch } : x)) });
  const addPlus = () => { if (plus.length < allowance) setAns({ plus_ones: [...plus, { name: "", meal_choice: "", dietary: "" }] }); };
  const removePlus = (i) => setAns({ plus_ones: plus.filter((_, idx) => idx !== i) });

  return (
    <div className="bg-white border border-[#E8E6E1] rounded-xl p-6 sm:p-8" data-testid={`rsvp-card-${guest.id}`}>
      <p className="overline mb-1">RSVP for</p>
      <h3 className="font-serif text-2xl mb-4">{guest.first_name} {guest.last_name}</h3>
      <div className="grid grid-cols-2 gap-3 mb-5">
        <ChoiceBtn active={ans.status === "attending"} onClick={() => setAns({ status: "attending" })} icon={Check} label="Joyfully accept" testid={`rsvp-accept-${guest.id}`} accent />
        <ChoiceBtn active={ans.status === "declined"} onClick={() => setAns({ status: "declined" })} icon={X} label="Regretfully decline" testid={`rsvp-decline-${guest.id}`} />
      </div>

      {ans.status === "attending" && (
        <div className="space-y-5 fade-up">
          {cfg?.meal && (
            <div>
              <span className="text-xs font-medium text-stone-500 mb-1.5 block">Meal preference</span>
              <select value={ans.meal_choice} onChange={(e) => setAns({ meal_choice: e.target.value })} data-testid={`rsvp-meal-${guest.id}`}
                className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]">
                <option value="">Select a meal…</option>
                {meals.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          )}
          {cfg?.dietary && <YesNoField label="Any dietary needs or allergies?" value={ans.dietary} onChange={(v) => setAns({ dietary: v })} testid={`rsvp-dietary-${guest.id}`} placeholder="Please tell us the details" />}
          {cfg?.song && <Field label="Song request" value={ans.song_request} onChange={(v) => setAns({ song_request: v })} testid={`rsvp-song-${guest.id}`} />}
          {cfg?.accessibility && <YesNoField label="Any accessibility needs?" value={ans.accessibility} onChange={(v) => setAns({ accessibility: v })} testid={`rsvp-accessibility-${guest.id}`} placeholder="Please describe how we can help" />}

          {cfg?.plus_one && allowance > 0 && (
            <div className="space-y-3">
              <span className="text-xs font-medium text-stone-500 block">Guests joining you (up to {allowance})</span>
              {plus.map((po, i) => (
                <div key={i} data-testid={`rsvp-plus-card-${guest.id}-${i}`} className="rounded-lg border border-[#E8E6E1] bg-[#FDFBF7] p-3 space-y-2">
                  <div className="flex gap-2">
                    <input placeholder={`Guest ${i + 1} full name`} data-testid={`rsvp-plus-${guest.id}-${i}`} value={po.name}
                      onChange={(e) => setPlus(i, { name: e.target.value })}
                      className="flex-1 rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
                    <button onClick={() => removePlus(i)} data-testid={`rsvp-plus-remove-${guest.id}-${i}`} className="px-2 text-stone-400 hover:text-[#C67A7A]"><Trash2 size={15} /></button>
                  </div>
                  {cfg?.meal && (
                    <select value={po.meal_choice} onChange={(e) => setPlus(i, { meal_choice: e.target.value })} data-testid={`rsvp-plus-meal-${guest.id}-${i}`}
                      className="w-full rounded-md border border-[#E8E6E1] bg-white px-3 py-2 text-sm outline-none focus:border-[#8C9A86]">
                      <option value="">Meal preference…</option>
                      {meals.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  )}
                  {cfg?.dietary && (
                    <input placeholder="Dietary needs or allergies (optional)" value={po.dietary} onChange={(e) => setPlus(i, { dietary: e.target.value })} data-testid={`rsvp-plus-dietary-${guest.id}-${i}`}
                      className="w-full rounded-md border border-[#E8E6E1] bg-white px-3 py-2 text-sm outline-none focus:border-[#8C9A86]" />
                  )}
                </div>
              ))}
              {plus.length < allowance && (
                <button onClick={addPlus} data-testid={`rsvp-add-plus-${guest.id}`} className="flex items-center gap-1.5 text-sm text-[#8C9A86] hover:underline">
                  <Plus size={14} /> Add a guest
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {ans.status && (
        <div className="mt-5"><Field label="A note to the host (optional)" value={ans.message} onChange={(v) => setAns({ message: v })} testid={`rsvp-message-${guest.id}`} /></div>
      )}
    </div>
  );
}

function ChoiceBtn({ active, onClick, icon: Icon, label, testid, accent }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex flex-col items-center gap-2 rounded-lg border py-5 text-sm transition-all duration-200 ${
        active ? (accent ? "border-[#8C9A86] bg-[#8C9A86]/10 text-[#5f6b59]" : "border-[#C67A7A] bg-[#C67A7A]/10 text-[#9c5757]")
               : "border-[#E8E6E1] text-stone-500 hover:border-stone-300"}`}>
      <Icon size={20} /> {label}
    </button>
  );
}

function YesNoField({ label, value, onChange, testid, placeholder }) {
  const [yes, setYes] = useState(!!(value && String(value).trim()));
  const pick = (v) => { setYes(v); if (!v) onChange(""); };
  return (
    <div data-testid={`${testid}-field`}>
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <div className="flex gap-2">
        <button type="button" onClick={() => pick(true)} data-testid={`${testid}-yes`}
          className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${yes ? "border-[#8C9A86] bg-[#8C9A86]/10 text-[#5f6b59]" : "border-[#E8E6E1] text-stone-500 hover:bg-stone-50"}`}>Yes</button>
        <button type="button" onClick={() => pick(false)} data-testid={`${testid}-no`}
          className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${!yes ? "border-[#8C9A86] bg-[#8C9A86]/10 text-[#5f6b59]" : "border-[#E8E6E1] text-stone-500 hover:bg-stone-50"}`}>No</button>
      </div>
      {yes && (
        <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
          className="mt-2 w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86] focus:ring-2 focus:ring-[#8C9A86]/20" />
      )}
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86] focus:ring-2 focus:ring-[#8C9A86]/20" />
    </label>
  );
}
function toPlusList(p) {
  if (Array.isArray(p.plus_ones_detail) && p.plus_ones_detail.length)
    return p.plus_ones_detail.map((d) => ({ name: d.name || "", meal_choice: d.meal_choice || "", dietary: d.dietary || "" }));
  return (p.plus_ones || []).map((n) => (typeof n === "string" ? { name: n, meal_choice: "", dietary: "" } : { name: n?.name || "", meal_choice: n?.meal_choice || "", dietary: n?.dietary || "" }));
}
function Centered({ children }) {
  return <div className="min-h-screen grid place-items-center" style={{ background: "#FDFBF7" }}>{children}</div>;
}
function formatDate(d) {
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return d; }
}
